import { useApolloClient } from "@apollo/client";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { CancelButton, SubmitButton } from "Components/Button";
import { CollapsibleSection } from "Components/CollapsibleSection";
import { Horizontal } from "Components/Horizontal";
import { SmallIconButton } from "Components/Icon";
import { InformationRow } from "Components/InformationRows";
import { Modal } from "Components/Modal";
import { useModalFormState } from "Components/ModalState";
import { Section } from "Components/Section";
import { format as formatDate } from "date-fns";
import {
  ClaimClosureStatus,
  updateClaimStatusMutation,
} from "generated/graphql";
import { DateInput } from "Inputs/DateInput";
import { Dropdown } from "Inputs/Dropdown";
import { useFormWithSchema } from "Inputs/FormTools";
import { DollarsAndCentsInput } from "Inputs/NumberInput";
import { Textarea } from "Inputs/Textarea";
import { YupDefined } from "Inputs/Tools";
import React from "react";
import {
  formatDollarsAndCents,
  hasValue,
  formatShortUIDate,
  sortBy,
  no,
  hv,
} from "Tools";
import { ClaimFileType } from "Views/LcpWorkflow/FileClaimPage";
import * as Yup from "yup";
import { getClaimStatus } from "./Tools";
import { Application, Claim } from "./Types";

function UpdateClaimStatusControlWithModal(props: {
  app: Application & { claim: Claim };
}) {
  const apolloClient = useApolloClient();
  const form = useFormWithSchema({
    initialValues: {
      claimClosedOn: props.app.claim.closedOn ?? new Date(),
      claimStatus: props.app.claim.closureStatus ?? null,
      claimPayoutAmount: props.app.claim.payoutAmount ?? null,
      claimNumber: props.app.claim.claimNumber ?? null,
      claimPayoutDate: props.app.claim.payoutDate ?? new Date(),
    },
    onSubmit: async values => {
      await apolloClient.mutate({
        mutation: updateClaimStatusMutation,
        variables: {
          input: {
            securityKey: props.app.securityKey,
            closureStatus: values.claimStatus,
            closedOn: values.claimClosedOn,
            payoutAmount: values.claimPayoutAmount,
            payoutDate: values.claimPayoutDate,
            claimNumber: values.claimNumber,
          },
        },
      });
    },
    validationSchema: Yup.object({
      claimStatus: YupDefined.enum(ClaimClosureStatus, null)
        .label("Claim Status")
        .required(),
      claimClosedOn: DateInput.getDefaultValidator()
        .label("Claim Closed On")
        .required(),
      claimPayoutAmount: DollarsAndCentsInput.getDefaultValidator()
        .label("Claim Payout Amount")
        .required()
        .when("claimStatus", {
          is: ClaimClosureStatus.DENIED,
          then: schema => schema.notRequired(),
        }),
      claimNumber: Textarea.getDefaultValidator().label("Claim No").required(),
      claimPayoutDate: DateInput.getDefaultValidator()
        .label("Claim Payout Date")
        .required()
        .min(
          props.app.claim.dateSubmitted,
          "Please enter a date on or after the Claim Submitted date.",
        )
        .when("claimStatus", {
          is: ClaimClosureStatus.DENIED,
          then: schema => schema.notRequired(),
        }),
    }),
  });
  const modalState = useModalFormState({ form });
  return (
    <>
      <Modal
        title="Update Claim Status"
        {...modalState}
        buttonRow={
          <>
            <CancelButton
              disabled={form.isSubmitting}
              onClick={() => modalState.close()}
            />
            <SubmitButton
              disabled={!form.dirty}
              isSubmitting={form.isSubmitting}
              onClick={() => modalState.submitFormAndClose()}
            />
          </>
        }
      >
        <Section
          title={props.app.purchaseRecord?.policyNumber}
          subtitle={
            <>
              <div>{props.app.case?.caseName}</div>
              <div>
                Claim Submitted On:{" "}
                {formatShortUIDate(props.app.claim.dateSubmitted)}
              </div>
            </>
          }
          className="narrowFieldBodies1"
        >
          <div style={{ textAlign: "center", marginBottom: "1em" }}>
            <em>Please enter the details of the claim resolution.</em>
          </div>
          <Horizontal {...form.field.claimStatus.getLabelFromSchema()}>
            <Dropdown
              field={form.field.claimStatus}
              options={[
                [ClaimClosureStatus.PAID, "Paid"],
                [ClaimClosureStatus.DENIED, "Denied"],
              ]}
              placeholder="Select status..."
            />
          </Horizontal>
          <Horizontal {...form.field.claimClosedOn.getLabelFromSchema()}>
            <DateInput field={form.field.claimClosedOn} />
          </Horizontal>
          {form.field.claimStatus.value === ClaimClosureStatus.PAID && (
            <Horizontal {...form.field.claimPayoutAmount.getLabelFromSchema()}>
              <DollarsAndCentsInput
                field={form.field.claimPayoutAmount}
                isDisabled={
                  form.field.claimStatus.value !== ClaimClosureStatus.PAID
                }
              />
            </Horizontal>
          )}
          <Horizontal {...form.field.claimNumber.getLabelFromSchema()}>
            <Textarea
              field={form.field.claimNumber}
              placeholder="Claim Number"
              textRows={1}
              maxLength={50}
            />
          </Horizontal>
          {form.field.claimStatus.value === ClaimClosureStatus.PAID && (
            <Horizontal {...form.field.claimPayoutDate.getLabelFromSchema()}>
              <DateInput
                field={form.field.claimPayoutDate}
                isDisabled={
                  form.field.claimStatus.value !== ClaimClosureStatus.PAID
                }
              />
            </Horizontal>
          )}
        </Section>
      </Modal>
      <SmallIconButton
        buttonStyle={{ paddingTop: 0 }}
        onClick={() => modalState.open()}
        icon={faEdit}
      />
    </>
  );
}

export function ClaimsCollapsible(props: {
  app: Application & { claim: Claim };
}) {
  return (
    <CollapsibleSection
      title={<>Claim</>}
      theme={{ header: { className: "is-secondary" } }}
    >
      <InformationRow label="Claim Contact" shouldTopAlign>
        <div>{props.app.claim.contactName}</div>
        <div>{props.app.claim.contactEmail}</div>
      </InformationRow>
      <InformationRow label="Claim Submitted on">
        {formatDate(props.app.claim.dateSubmitted, "MMMM d, yyyy")}
      </InformationRow>
      <InformationRow label="Claim No">
        {props.app.claim.claimNumber ?? "TBD"}
      </InformationRow>
      {hasValue(props.app.claim.claimAmount) && (
        <InformationRow label="Claim Amount">
          {formatDollarsAndCents(props.app.claim.claimAmount)}
        </InformationRow>
      )}
      <InformationRow label="Claim Status">
        {getClaimStatus(props.app.claim)}{" "}
        <UpdateClaimStatusControlWithModal {...props} />
      </InformationRow>
      {props.app.claim.lastUpdatedByAdmin && (
        <InformationRow label="Claim Updated By">
          {props.app.claim.lastUpdatedByAdmin.fullName}
        </InformationRow>
      )}
      {hv(props.app.claim.payoutAmount) && (
        <InformationRow label="Claim Payout Amount">
          {formatDollarsAndCents(props.app.claim.payoutAmount)}
        </InformationRow>
      )}
      {hv(props.app.claim.payoutDate) && (
        <InformationRow label="Claim Payout Date">
          {formatShortUIDate(props.app.claim.payoutDate)}
        </InformationRow>
      )}
      {hv(props.app.claim.closedOn) && (
        <InformationRow label="Closed On">
          {formatShortUIDate(props.app.claim.closedOn)}
        </InformationRow>
      )}
      <ClaimFiles
        claim={props.app.claim}
        policyNumber={props.app.purchaseRecord?.policyNumber ?? no()}
      />
    </CollapsibleSection>
  );
}

function ClaimFiles({
  claim,
  policyNumber,
}: {
  claim: Claim;
  policyNumber: string;
}) {
  const claimFileListInputs = [
    {
      list: claim.adverseJudgmentFiles,
      name: ClaimFileType.AdverseJudgementFile,
    },
    {
      list: claim.litigationCostInvoicesFiles,
      name: ClaimFileType.LitigationCostInvoicesFile,
    },
    {
      list: claim.litigationCostListingFiles,
      name: ClaimFileType.LitigationCostListingFile,
    },
    { list: claim.proofOfPaymentFiles, name: ClaimFileType.ProofOfPaymentFile },
    { list: claim.insuredW9Files, name: ClaimFileType.InsuredW9File },
  ];

  return (
    <div style={{ fontWeight: "bold", textAlign: "center" }}>
      {claimFileListInputs.map(input => (
        <ClaimFileList
          key={input.name}
          claimFileList={input.list}
          fileTypeName={input.name}
          policyNumber={policyNumber}
        />
      ))}
    </div>
  );
}

function ClaimFileList({
  claimFileList,
  fileTypeName,
  policyNumber,
}: {
  claimFileList: { fileStorageId: number }[];
  fileTypeName: string;
  policyNumber: string;
}) {
  return (
    <>
      {claimFileList.toSorted(sortBy(f => f.fileStorageId)).map((f, i, arr) => {
        const linkText = `${fileTypeName} - (${i + 1} of ${arr.length})`;
        return (
          <p key={f.fileStorageId}>
            <a
              target="_blank"
              rel="noreferrer"
              href={`/api/fileStorage/${f.fileStorageId}?filename=${policyNumber} - ${linkText}`}
            >
              {linkText}
            </a>
          </p>
        );
      })}
    </>
  );
}
